/* eslint-disable react/no-unescaped-entities */
import {
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderTitle,
  Steps,
  Text,
  notification,
} from '@antoree/ant-ui';
import { Session } from 'services/session';
import React, { useState } from 'react';
import { useAnnounceStudent } from 'services/session';
import { useCheckIn } from 'services/session/checkIn';
import { useCheckOut } from 'services/session/checkOut';
import { useStartClass } from 'services/session/startClass';
import { useReportCC } from 'services/session/reportCC';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { useToggle } from 'hooks';
import Notice from 'components/Notice';
import { useScreenRecorder } from 'contexts/ScreenRecorderContext';
import { useCreateLiveKitRoom, useEndLiveKitClass } from 'services/videoCall';
import { MEET_URL } from 'configs/env.conf';
import { encryptToken, ENCRYPTION_KEY } from 'utils/encryption';

export type AttendanceModalProps = {
  session: Session;
  view: boolean;
  allowSubmit: boolean;
  onReload: (session: Session) => void;
  close: () => void;
};
const AttendanceModal = ({
  session,
  view,
  allowSubmit,
  close,
  onReload,
}: AttendanceModalProps) => {
  const intl = useIntl();
  const [isStartingRecording, setIsStartingRecording] = useState(false);
  const {
    startRecording,
    stopRecording,
    requestStopRecording,
    recordingStatus,
  } = useScreenRecorder();
  const {
    isVisiable: isVisibleNotice,
    open: openNotice,
    close: closeNotice,
  } = useToggle();

  const {
    isVisiable: isVisibleJoinCall,
    open: openJoinCall,
    close: closeJoinCall,
  } = useToggle();

  const { isVisiable: isVisibleCCPopup, open: openCCPopup, close: closeCCPopup } = useToggle();
  const [hasShownPopup5, setHasShownPopup5] = useState(false);
  const [hasShownPopup15, setHasShownPopup15] = useState(false);
  const [devInfo, setDevInfo] = useState<any>(null);
  const [isMuted, setIsMuted] = useState(false);

  // CC Flow useEffect moved down to fix TS2448
  const playAlert = () => {
    try {
      const audio = new Audio('/audios/notification_message-notification-6-337829.mp3');
      audio.volume = 1.0;
      audio.play().catch(e => console.log('Audio play failed', e));
    } catch (error) {}
  };

  const { mutate: mutateReportCC, isLoading: isLoadingReportCC } = useReportCC({
    onSuccess: data => {
      onReload(data?.data?.session);
      closeCCPopup();
      notification.success({
        title: <FormattedMessage id="attendance.report_cc_success" defaultMessage="Đã báo cáo Customer Care thành công!" />,
      });
    },
    onError: error => {
      notification.error({
        title: <FormattedMessage id="attendance.report_cc_error" defaultMessage="Có lỗi xảy ra khi báo CC" />,
        text: <p>{error?.response?.data?.errors?.[0]?.message || 'Vui lòng thử lại'}</p>,
      });
    },
  });

  const { mutate: mutateCheckIn, mutateAsync: mutateAsyncCheckIn, isLoading: isLoadingCheckIn } = useCheckIn({
    onSuccess: data => {
      onReload(data?.data?.session);
      notification.success({
        title: <FormattedMessage defaultMessage="Check-in successful" />,
      });
    },
    onError: error => {
      notification.error({
        title: <FormattedMessage defaultMessage="Check-in failed" />,
        text: <p>{error?.response?.data?.errors[0]?.message}</p>,
      });
    },
  });

  const { mutate: mutateCheckOut, isLoading: isLoadingCheckOut } = useCheckOut({
    onSuccess: data => {
      onReload(data?.data?.session);
      notification.success({
        title: <FormattedMessage defaultMessage="Check-out successful" />,
      });
      stopRecording();
      closeNotice();
      close();
    },
    onError: error => {
      notification.error({
        title: <FormattedMessage defaultMessage="Check-out failed" />,
        text: <p>{error?.response?.data?.errors[0]?.message}</p>,
      });
    },
  });

  const {
    mutate: mutateStartClass,
    isLoading: isLoadingStartClass,
  } = useStartClass({
    onSuccess: data => {
      onReload(data?.data?.session);
      closeCCPopup();
      notification.success({
        title: <FormattedMessage defaultMessage="Start class successful" />,
      });
    },
    onError: error => {
      notification.error({
        title: <FormattedMessage defaultMessage="Start class failed" />,
        text: <p>{error?.response?.data?.errors[0]?.message}</p>,
      });
    },
  });

  const {
    mutate: mutateAnnounceStudent,
    isLoading: isLoadingAnnounceStudent,
  } = useAnnounceStudent({
    onSuccess: data => {
      onReload(data?.data?.session);
      notification.success({
        title: <FormattedMessage defaultMessage="Notify student successful" />,
      });
    },
    onError: error => {
      notification.error({
        title: <FormattedMessage defaultMessage="Notify student failed" />,
        text: <p>{error?.response?.data?.errors[0]?.message}</p>,
      });
    },
  });

  const {
    mutate: createRoom,
    isLoading: isCreatingRoom,
  } = useCreateLiveKitRoom({
    onSuccess: async (response: any) => {
      const { roomId, token, livekitUrl } = response?.data?.data || {};
      if (roomId && token) {
        const encryptedToken = await encryptToken(token, ENCRYPTION_KEY);
        const callUrl = `${MEET_URL}/room/${roomId}?token=${encodeURIComponent(
          encryptedToken,
        )}&accessToken=${encodeURIComponent(token)}&url=${encodeURIComponent(
          livekitUrl || 'wss://livekit.antoree.com',
        )}`;
        window.open(callUrl, '_blank');
        closeJoinCall();
      } else {
        notification.error({
          title: <FormattedMessage defaultMessage="Failed to create room" />,
          text: (
            <FormattedMessage defaultMessage="Invalid response from server" />
          ),
        });
        closeJoinCall();
      }
    },
    onError: err => {
      notification.error({
        title: <FormattedMessage defaultMessage="Failed to create room" />,
        text: err?.response?.data?.message || err.message,
      });
      closeJoinCall();
    },
  });

  const { mutate: endClass, isLoading: isEndingClass } = useEndLiveKitClass({
    onError: err => {
      console.error('Failed to end class', err);
      createRoom({
        studentIds: [(session?.student as any)?.id],
        callType: 'video',
      });
    },
    onSuccess: () => {
      createRoom({
        studentIds: [(session?.student as any)?.id],
        callType: 'video',
      });
    },
  });

  const handleJoinVideoCall = () => {
    endClass();
  };

  /* DEV MODE - Temporarily disabled
  React.useEffect(() => {
    if (!session || session.studentCheckIn) return;
    const interval = setInterval(() => {
      const now = moment();
      const start = moment(session.occurredAt);
      const diffMinutes = now.diff(start, 'minutes', true);
      
      const info = {
        currentTime: now.format('HH:mm:ss'),
        startTime: start.format('HH:mm:ss'),
        diffMinutes: diffMinutes.toFixed(2),
        hasShownPopup5,
        hasShownPopup15,
        teacherCheckIn: session.teacherCheckIn
      };
      
      setDevInfo(info);
      console.log(`[Dev - CC Flow] Checking trigger:`, info);

      if (diffMinutes >= 0 && diffMinutes <= 30) {
        if (diffMinutes < 5 && !hasShownPopup5) {
          console.log('[Dev - CC Flow] Triggering 0-5 min popup');
          setHasShownPopup5(true);
          openCCPopup();
          if (!isMuted) playAlert();
        } else if (diffMinutes >= 5 && diffMinutes <= 30 && !hasShownPopup15) {
          console.log('[Dev - CC Flow] Triggering 5-30 min popup');
          setHasShownPopup15(true);
          openCCPopup();
          if (!isMuted) playAlert();
        }
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [session, hasShownPopup5, hasShownPopup15, openCCPopup, isMuted]);
  */

  // Handle modal close - if recording, stop it first
  const handleClose = () => {
    close();
  };

  return (
    <div>
      <Modal onClose={handleClose} style={{ minWidth: '70vw' }}>
        <ModalHeader>
          <ModalHeaderTitle>
            <h1>
              <FormattedMessage defaultMessage="Attendance" />
            </h1>
          </ModalHeaderTitle>
        </ModalHeader>

        <ModalBody>
          <style>{`
            @keyframes alert-pulse {
              0% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.7); }
              70% { box-shadow: 0 0 0 15px rgba(255, 77, 79, 0); }
              100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0); }
            }
            .alert-pulse-animation {
              animation: alert-pulse 1.5s infinite;
              border: 3px solid #ff4d4f !important;
              background-color: #fff1f0;
            }
          `}</style>
          {/* DEV MODE - Temporarily hidden
          {devInfo && (
            <div className="bg-danger-light p-2 mb-4 rounded border" style={{ borderColor: '#ff4d4f' }}>
              <Text size="s" className="font-bold text-danger mb-1">[DEV MODE] Trạng thái check báo CC tự động:</Text>
              <label className="flex items-center gap-2 mb-2">
                <input type="checkbox" checked={isMuted} onChange={(e) => setIsMuted(e.target.checked)} />
                <Text size="s">Tắt âm thanh cảnh báo (Mute)</Text>
              </label>
              <pre className="text-xs m-0">{JSON.stringify(devInfo, null, 2)}</pre>
            </div>
          )}
          */}
          {isVisibleNotice && (
            <Notice
              close={closeNotice}
              content={intl.formatMessage({
                defaultMessage:
                  'You end the class before the scheduled time, your salary is calculated from the "Start class" time to the "End class" time. Are you sure you want to end this class?',
              })}
              title={intl.formatMessage({ defaultMessage: 'Notice' })}
              func={() => mutateCheckOut({ id: session.id })}
              isLoading={isLoadingCheckOut}
            />
          )}
          <Text>
            <FormattedMessage defaultMessage="Session" /> #{session.id}{' '}
            <FormattedMessage defaultMessage="occurred at" />{' '}
            {moment(session.occurredAt).format('DD-MM-YYYY HH:mm')}
          </Text>
          <Text className="mb-4">
            <FormattedMessage defaultMessage="Keep this open during your class to ensure you are not missing anything." />
          </Text>
          <div className="bg-warning-light mb-4 rounded-lg ">
            <Text className="p-4 ">
              <Icon type="flag" color="red" />
              <FormattedMessage defaultMessage="You must keep the camera on throughout the entire session. Otherwise, you will receive a penalty of 50% of the session if student reports it to us." />
            </Text>
          </div>
          {isVisibleJoinCall && (
            <Notice
              close={closeJoinCall}
              content={intl.formatMessage({
                id: 'attendance.confirm_join_video_call_content',
                defaultMessage:
                  'Recording started. do you want to start video call?',
              })}
              title={intl.formatMessage({
                id: 'attendance.confirm_join_video_call_title',
                defaultMessage: 'Join Video Call',
              })}
              func={() => {
                handleJoinVideoCall();
              }}
              isLoading={isCreatingRoom || isEndingClass}
            />
          )}
          <Text className="mb-4 text-danger text-lg italic">
            <FormattedMessage
              id="attendance.add_content_warning"
              defaultMessage='You have to "add content" within 10 hours since the class ended to receive 100% salary rate'
            />
          </Text>

          {/* DEV MODE - Temporarily hidden
          {isVisibleCCPopup && (
            <div className="alert-pulse-animation p-6 mb-6 rounded-lg shadow-lg flex flex-col items-center text-center">
              <Icon type="alert" color="#ff4d4f" className="mb-2 w-12 h-12" />
              <h2 className="text-danger font-bold text-2xl mb-2">
                {intl.locale === 'vi-VN' ? `LỚP HỌC ĐÃ ĐẾN GIỜ!` : `CLASS IS STARTING!`}
              </h2>
              <h3 className="font-bold text-lg mb-2">
                {intl.locale === 'vi-VN' ? `Học viên đã vào lớp lúc ${moment(session?.occurredAt).format('HH:mm')} chưa?` : `Has the student entered the ${moment(session?.occurredAt).format('HH:mm')} class?`}
              </h3>
              <p className="mb-6 text-subdued text-base">
                {intl.locale === 'vi-VN' ? `Bắt buộc phải báo cáo CC hoặc Start lớp ngay lập tức.` : `You must Report CC or Start the class immediately.`}
              </p>
              <div className="flex gap-4 w-full justify-center">
                <Button
                  fill
                  style={{ backgroundColor: '#14B24C', borderColor: '#14B24C' }}
                  onClick={async () => {
                    if (!session?.teacherCheckIn) {
                      try {
                        await mutateAsyncCheckIn({ id: session?.id });
                        mutateStartClass({ id: session?.id });
                      } catch (error) {
                        // Error is already handled by useCheckIn's onError
                      }
                    } else {
                      mutateStartClass({ id: session?.id });
                    }
                  }}
                  isLoading={isLoadingStartClass || isLoadingCheckIn}
                >
                  {intl.locale === 'vi-VN' ? 'ĐÃ VÀO - START CLASS' : 'YES - START CLASS'}
                </Button>
                <Button
                  color="primary"
                  onClick={() => mutateReportCC({ id: session?.id as number })}
                  isLoading={isLoadingReportCC}
                >
                  {intl.locale === 'vi-VN' ? 'CHƯA VÀO - BÁO CC' : 'NO - REPORT CC'}
                </Button>
              </div>
            </div>
          )}
          */}

          <Steps
            steps={[
              {
                title: 'Teacher shows up',
                status: 'disabled',
                onClick: () => {},
                children: (
                  <>
                    <Text>
                      <FormattedMessage defaultMessage="Press 'Show up' to confirm that You've entered the classroom and ready to teach" />
                    </Text>
                    <Text className="text-danger ">
                      <FormattedMessage
                        id="attendance.call_student_reminder"
                        defaultMessage="Kindly call your student at least 3 times to make sure that they were alerted and ready for class"
                      />
                    </Text>

                    <div className="flex items-center gap-4 mt-4">
                      <Button
                        fill
                        iconType="documentEdit"
                        onClick={() => mutateCheckIn({ id: session?.id })}
                        disabled={!!session.teacherCheckIn}
                        isLoading={isLoadingCheckIn}
                      >
                        <FormattedMessage defaultMessage="Show up" />
                      </Button>
                      {session.teacherCheckIn && (
                        <Text>
                          <FormattedMessage defaultMessage="You Showed up at" />{' '}
                          {moment(session.teacherCheckIn).format(
                            'HH:mm DD-MM-YYYY',
                          )}
                        </Text>
                      )}
                    </div>
                    {session.teacherCheckIn && (
                      <>
                        <Text className="mt-4">
                          <FormattedMessage
                            id="attendance.record_session_instruction"
                            defaultMessage="Press 'Record session' to start recording"
                          />
                        </Text>
                        <div className="flex items-center gap-4 mt-1">
                          <Button
                            iconType="videoPlayer"
                            style={{
                              backgroundColor:
                                recordingStatus === 'recording'
                                  ? '#ff4d4f'
                                  : 'transparent',
                              border: `1px solid #ff4d4f`,
                              color:
                                recordingStatus === 'recording'
                                  ? '#fff'
                                  : '#ff4d4f',
                            }}
                            // isLoading={
                            //   isCreatingRoom ||
                            //   isEndingClass ||
                            //   isStartingRecording
                            // }
                            isLoading={isStartingRecording}
                            onClick={async () => {
                              if (recordingStatus === 'recording') {
                                requestStopRecording();
                              } else {
                                try {
                                  setIsStartingRecording(true);
                                  await startRecording(
                                    String(session?.id),
                                    String(session?.course_id),
                                    'English',
                                    `[Buổi học] ${
                                      session?.title ||
                                      `Session #${session?.id}`
                                    }`,
                                  );
                                  // openJoinCall();
                                } finally {
                                  setIsStartingRecording(false);
                                }
                              }
                            }}
                          >
                            {recordingStatus === 'recording' ? (
                              <FormattedMessage defaultMessage="Stop recording" />
                            ) : (
                              <FormattedMessage defaultMessage="Record session" />
                            )}
                          </Button>
                          {session.recorded_at && (
                            <Text>
                              <FormattedMessage defaultMessage="Recorded at" />{' '}
                              {moment(session.recorded_at).format(
                                'HH:mm DD-MM-YYYY',
                              )}
                            </Text>
                          )}
                        </div>
                        {recordingStatus !== 'recording' && (
                          <div className="mt-2">
                            <Text
                              size="s"
                              style={{
                                color: '#ff4d4f',
                                fontStyle: 'italic',
                                fontWeight: 500,
                              }}
                            >
                              <FormattedMessage
                                id="attendance.record_warning"
                                defaultMessage='Note: Select "ENTIRE SCREEN" and check "SHARE SYSTEM AUDIO" to record.'
                              />
                            </Text>
                          </div>
                        )}
                      </>
                    )}
                  </>
                ),
              },
              {
                title: 'Student shows up',
                status: 'disabled',
                onClick: () => {},
                children: (
                  <>
                    <Text className="mb-2">
                      <FormattedMessage
                        id="attendance.start_class_instruction"
                        defaultMessage='Press "Start class" to confirm your student already presented in class and you have started teaching.'
                      />
                    </Text>
                    <Text className="text-danger mb-2">
                      <FormattedMessage defaultMessage="You are not allowed to press the 'Start class' button if the students haven't entered the class." />
                    </Text>
                    <div className="flex items-center gap-4 flex-wrap">
                      <Button
                        fill
                        iconType="documentEdit"
                        className="mt-2"
                        onClick={() => mutateStartClass({ id: session?.id })}
                        isLoading={isLoadingStartClass}
                        isDisabled={
                          !!session.studentCheckIn || !session.teacherCheckIn
                        }
                      >
                        <FormattedMessage defaultMessage="Start class" />
                      </Button>
                      {session.studentCheckIn && (
                        <Text className="mt-2">
                          <FormattedMessage defaultMessage="Your student show up at " />
                          {moment(session.studentCheckIn).format(
                            'HH:mm DD-MM-YYYY',
                          )}
                        </Text>
                      )}
                      {session.reported_cc_at && !session.studentCheckIn && (
                        <Text className="mt-2 text-danger">
                          <FormattedMessage id="attendance.reported_cc_at" defaultMessage="Đã báo CC lúc: " />
                          {moment(session.reported_cc_at).format('HH:mm DD-MM-YYYY')}
                        </Text>
                      )}
                    </div>
                  </>
                ),
              },

              {
                title: 'End class',
                status: 'disabled',
                onClick: () => {},
                children: (
                  <>
                    <Text>
                      <FormattedMessage
                        id="attendance.end_class_instruction"
                        defaultMessage='Press "End class" only when the class ends up.'
                      />
                    </Text>
                    <div className="flex items-center gap-4">
                      <Button
                        fill
                        iconType="documentEdit"
                        className="mt-4"
                        onClick={() => openNotice()}
                        disabled={
                          !session.studentCheckIn || !!session.teacherCheckOut
                        }
                      >
                        <FormattedMessage defaultMessage="End class" />
                      </Button>
                      {session.teacherCheckOut && (
                        <Text className="mt-4">
                          <FormattedMessage defaultMessage="Ended class at " />
                          {moment(session.teacherCheckOut).format(
                            'HH:mm DD-MM-YYYY',
                          )}
                        </Text>
                      )}
                    </div>
                  </>
                ),
              },
            ]}
          />
        </ModalBody>

        <ModalFooter>
          <div>
            <span>
              <FormattedMessage defaultMessage="For further information" />
            </span>
            <span>
              <a
                className="text-success-dark ml-1"
                href="https://freshdesk.antoree.com/support/solutions/articles/67000722778-new-attendance-feature"
                target="_blank"
              >
                <FormattedMessage defaultMessage="here" />
              </a>
            </span>{' '}
          </div>
          <Button onClick={close} color="text" className="ml-auto">
            <FormattedMessage defaultMessage="Close" />
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AttendanceModal;
